import {
  BMI_STATUS,
  calculateBmi,
  calculateBmiResult,
  calculateHealthRisk,
  calculateWhtrResult,
  classifyBmi,
  classifyWhtr,
  WHTR_STATUS,
} from './healthCalculations';

export { calculateBmi, calculateHealthRisk };

export const calculateWaistHeightRatio = (waistCm, heightCm) => {
  const w = parseFloat(waistCm);
  const h = parseFloat(heightCm);
  if (Number.isNaN(w) || Number.isNaN(h) || h <= 0 || w <= 0) return null;
  return Math.round((w / h) * 100) / 100;
};

export const sumScores = (section, keys) => {
  if (!section) return null;
  const values = keys
    .map((k) => section[k])
    .filter((v) => v !== '' && v != null)
    .map((v) => parseInt(v, 10));
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0);
};

export const calculateFmsRisk = (total) => {
  if (total == null) return null;
  if (total <= 10) return 'high';
  if (total <= 16) return 'moderate';
  return 'low';
};

export const calculateMobilityClass = (total) => {
  if (total == null) return null;
  if (total >= 20) return 'excellent';
  if (total >= 12) return 'moderate_restriction';
  return 'significant_restriction';
};

const POSTURE_NORMAL = {
  head: 'normal',
  shoulders: 'normal',
  thoracic_spine: 'normal',
  lumbar_spine: 'normal',
  knees: 'normal',
  feet: 'normal',
};

export const generateSummaryPreview = (values) => {
  const health = values.health_screening || {};
  const posture = values.posture || {};
  const fms = values.functional_movement || {};
  const mobility = values.mobility || {};

  const strengths = [];
  const improvements = [];
  const priorities = [];

  const bmiResult = calculateBmiResult(health.weight_kg, health.height_cm);
  const whtrResult = calculateWhtrResult(health.waist_cm, health.height_cm);
  const fmsTotal = sumScores(fms, Object.keys(fms));
  const fmsRisk = calculateFmsRisk(fmsTotal);
  const mobilityTotal = sumScores(mobility, Object.keys(mobility));
  const mobilityClass = calculateMobilityClass(mobilityTotal);

  if (bmiResult.valid && bmiResult.category === 'normal_weight') {
    strengths.push('BMI is within the normal weight range.');
  }
  if (whtrResult.valid && whtrResult.category === 'healthy') {
    strengths.push('Waist-to-height ratio is within the healthy range.');
  }
  if (fmsRisk === 'low') strengths.push('Good functional movement quality across screened patterns.');
  if (mobilityClass === 'excellent') strengths.push('Excellent mobility across assessed joints.');

  const postureDeviations = Object.keys(POSTURE_NORMAL).filter(
    (k) => posture[k] && posture[k] !== POSTURE_NORMAL[k]
  ).length;
  if (postureDeviations > 0) {
    improvements.push(`Posture deviations noted in ${postureDeviations} area(s).`);
    priorities.push('posture_correction');
  }

  if (bmiResult.valid && ['overweight', 'obesity_class_i', 'obesity_class_ii', 'obesity_class_iii'].includes(bmiResult.category)) {
    improvements.push('BMI indicates elevated body weight relative to height.');
    priorities.push('weight_loss');
  }
  if (bmiResult.valid && bmiResult.category === 'underweight') {
    improvements.push('BMI indicates underweight — nutritional assessment may be beneficial.');
  }
  if (whtrResult.valid && ['increased_risk', 'high_risk'].includes(whtrResult.category)) {
    improvements.push('Waist-to-height ratio indicates elevated central adiposity risk.');
    if (!priorities.includes('weight_loss')) priorities.push('weight_loss');
  }
  if (whtrResult.valid && whtrResult.category === 'underweight_risk') {
    improvements.push('Waist-to-height ratio suggests possible nutritional risk.');
  }

  if (fmsRisk === 'moderate' || fmsRisk === 'high') {
    improvements.push('Functional movement screening indicates movement quality concerns.');
    priorities.push('stability');
    if (fmsRisk === 'high') priorities.push('mobility');
  }
  if (mobilityClass === 'moderate_restriction' || mobilityClass === 'significant_restriction') {
    improvements.push('Mobility restrictions identified during assessment.');
    priorities.push('mobility');
  }

  if (!strengths.length) {
    strengths.push('Baseline assessment completed; individualized programming can now be prescribed.');
  }
  if (!improvements.length) {
    improvements.push('Continue monitoring and progressive overload in training.');
  }

  return {
    strengths: strengths.join('\n'),
    areas_for_improvement: improvements.join('\n'),
    corrective_priorities: [...new Set(priorities)],
    trainer_notes: values.summary?.trainer_notes || '',
  };
};

export { BMI_STATUS, WHTR_STATUS, classifyBmi, classifyWhtr };
